// documentation: lookup canvas-confetti via context7 or https://github.com/catdad/canvas-confetti

import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

// Connects to data-controller="confetti"
export default class extends Controller {
  static values = {
    animation: String,
    duration: { type: Number, default: 1 },  // Reduced to just 1 second for very fast animation
    firstColor: { type: String, default: "#0886DE" },
    secondColor: { type: String, default: "#FF6154" }
  }

  connect() {
    // Trigger confetti on page load
    setTimeout(() => {
      if (this.hasAnimationValue) {
        // Handle different animation types
        if (this.animationValue === "school-pride") {
          this.schoolPrideAnimation();
          
          // Add a second burst after a shorter delay for a more dramatic effect
          setTimeout(() => {
            this.schoolPrideAnimation();
          }, 150); // Reduced from 300ms to 150ms
        } else if (this.animationValue === "stars") {
          this.starsAnimation();
        } else if (this.animationValue === "fireworks") {
          this.fireworksAnimation();
        } else {
          this.basicAnimation();
        }
      } else {
        this.schoolPrideAnimation();
      }
    }, 100);
  }
  
  // Handle spray action from button clicks or other events
  spray(event) {
    if (this.hasAnimationValue) {
      // Handle different animation types
      if (this.animationValue === "school-pride") {
        this.schoolPrideAnimation();
      } else if (this.animationValue === "stars") {
        this.starsAnimation();
      } else if (this.animationValue === "fireworks") {
        this.fireworksAnimation();
      } else {
        this.basicAnimation(event);
      }
    } else {
      this.basicAnimation(event);
    }
    
    // If we're running this from a link with an href, let it navigate after the animation
    if (event?.currentTarget?.getAttribute("href")) {
      event.preventDefault();
      
      setTimeout(() => {
        window.location.href = event.currentTarget.getAttribute("href");
      }, this.durationValue * 1000);
    }
  }

  // Basic confetti spray from cursor position
  basicAnimation(event) {
    const origin = event ? { 
      x: event.clientX / window.innerWidth, 
      y: event.clientY / window.innerHeight 
    } : { x: 0.5, y: 0.5 };
    
    confetti({
      particleCount: 80,
      spread: 70,
      origin,
      colors: [this.firstColorValue, this.secondColorValue]
    });
  }
  
  // School pride animation with two side cannons
  schoolPrideAnimation() {
    const end = Date.now() + (this.durationValue * 1000);
    
    const frame = () => {
      confetti({
        particleCount: 20,       // Even more particles for a fuller effect
        angle: 60,
        spread: 70,              // Wide spread
        scalar: 1.0,             // Standard size for speed
        drift: 0,                // No horizontal drift
        gravity: 1.5,            // Much higher gravity for very fast fall
        startVelocity: 45,       // Much higher initial velocity
        ticks: 200,              // Fewer ticks means each particle lives for less time
        origin: { x: 0, y: 0.3 },
        colors: [this.firstColorValue, this.secondColorValue]
      });
      
      confetti({
        particleCount: 20,       // Even more particles for a fuller effect
        angle: 120,
        spread: 70,              // Wide spread
        scalar: 1.0,             // Standard size for speed
        drift: 0,                // No horizontal drift
        gravity: 1.5,            // Much higher gravity for very fast fall
        startVelocity: 45,       // Much higher initial velocity
        ticks: 200,              // Fewer ticks means each particle lives for less time
        origin: { x: 1, y: 0.3 },
        colors: [this.firstColorValue, this.secondColorValue]
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    frame();
  }
  
  // Stars animation
  starsAnimation() {
    const defaults = {
      spread: 360,
      ticks: 50,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      shapes: ['star'],
      colors: [this.firstColorValue, this.secondColorValue]
    };

    function shoot() {
      confetti({
        ...defaults,
        particleCount: 10,
        scalar: 1.5,
        shapes: ['star']
      });

      confetti({
        ...defaults,
        particleCount: 10,
        scalar: 0.9,
        shapes: ['star']
      });
    }

    setTimeout(shoot, 0);
    setTimeout(shoot, 100);
    setTimeout(shoot, 200);
  }
  
  // Fireworks animation
  fireworksAnimation() {
    const duration = this.durationValue * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { 
      startVelocity: 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 0,
      colors: [this.firstColorValue, this.secondColorValue]
    };

    const randomInRange = (min, max) => {
      return Math.random() * (max - min) + min;
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      // Since particles fall down, start a bit higher than random
      confetti({ 
        ...defaults, 
        particleCount, 
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } 
      });
      confetti({ 
        ...defaults, 
        particleCount, 
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } 
      });
    }, 250);
  }
}
